import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';

import type { IStats } from '@feathr/blackbox';
import { EUsageMetrics } from '@feathr/blackbox';
import { Chip, isSuperpixelActive, Tab, Tabs } from '@feathr/components';
import type { IPageProps } from '@feathr/extender/App/Page';
import Page from '@feathr/extender/App/Page';
import { useAccount, useLocalUrl, useUsageAndQuota } from '@feathr/extender/state';

import UsageStat, { usageMap } from './UsageStat';

import * as styles from './DataPage.css';

export interface IProps extends IPageProps {
  filters?: React.ReactNode;
  hasAnalytics?: boolean;
  hasTabs?: boolean;
  readOnly?: boolean;
  sectionTitle: string;
}

function DataPage({
  description,
  children,
  filters,
  hasAnalytics = false,
  hasTabs = true,
  readOnly = false,
  sectionTitle,
  title,
  ...props
}: IProps): JSX.Element {
  const account = useAccount();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();

  const { quota, usage, showUsage } = useUsageAndQuota(account.id, [
    EUsageMetrics.IncludedAnonymousPersons,
    EUsageMetrics.IncludedIdentifiedPersons,
    EUsageMetrics.Breadcrumbs,
  ]);

  // Is the superpixel set up?
  const stats = account.get('stats', { num_crumbs: 0 } as IStats);
  if (!account.isPending && !isSuperpixelActive(stats.num_crumbs || 0)) {
    return <Redirect to={localUrl('/data/pixel?nodata=1')} />;
  }

  const tabs = [
    <Tab key={'people'} link={'people'} title={t('People')} />,
    <Tab key={'activity'} link={'activity'} title={t('Activity')} />,
  ];
  if (hasAnalytics) {
    tabs.push(<Tab key={'analytics'} link={'analytics'} title={t('Analytics')} />);
  }

  return (
    <Page {...props} loading={account.isPending} title={sectionTitle}>
      {readOnly && (
        <section className={styles.metadata}>
          <Chip tooltip={t('Read-only groups cannot be edited.')}>{t('Read-only')}</Chip>
        </section>
      )}
      {hasTabs && <Tabs>{tabs}</Tabs>}
      <h3>{title}</h3>
      {showUsage && (
        <div className={styles.usageContainer} key={'usage'}>
          {Object.keys(usage).map(
            (key: string): JSX.Element => (
              <UsageStat
                key={key}
                limit={(quota[key] && numeral(quota[key]).format('0,0')) ?? t('unlimited')}
                t={t}
                title={usageMap(t, key as EUsageMetrics)}
                usage={numeral(usage[key]).format('0,0')}
              />
            ),
          )}
        </div>
      )}
      <>
        {!!filters && <div className={classNames(styles.filters)}>{filters}</div>}
        {!!description && <div className={styles.description}>{description}</div>}
        {children}
      </>
    </Page>
  );
}

export default observer(DataPage);
